import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GenericSearchControls from '../components/common/GenericSearchControls';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import purchaseOrdersService from '../services/purchase-orders-service';

const PO_STATUS_OPTIONS = [
  { value: 'Open', label: 'Open' },
  { value: 'Partial', label: 'Partial' },
  { value: 'Closed', label: 'Closed' }
];

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [statusFilter, setStatusFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });
  const [error, setError] = useState(null);
  const paginationLimitRef = useRef(20);

  useEffect(() => {
    paginationLimitRef.current = pagination.limit;
  }, [pagination.limit]);

  // restore saved search state
  useEffect(() => {
    const savedSearch = localStorage.getItem('poSearchValue');
    const savedFilters = localStorage.getItem('poAdvancedSearchFilters');
    if (savedSearch) setSearchValue(savedSearch);
    if (savedFilters) setFilters(JSON.parse(savedFilters));
  }, []);

  const fetchAllPurchaseOrders = useCallback(
    async (page = 1, limit = paginationLimitRef.current) => {
      setLoading(true);
      setError(null);
      try {
        const response = await purchaseOrdersService.getList(page, limit);
        if (response.success) {
          setPurchaseOrders(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPurchaseOrders([]);
          setError(response.message || 'Failed to load purchase orders.');
        }
      } catch (err) {
        console.error('Failed to fetch purchase orders:', err);
        setError('Failed to load purchase orders. Please try again.');
      }
      setLoading(false);
    },
    [] // stable — reads limit from paginationLimitRef
  );

  useEffect(() => {
    fetchAllPurchaseOrders();
  }, [fetchAllPurchaseOrders]);

  useEffect(() => {
    localStorage.setItem('poSearchValue', searchValue);
  }, [searchValue]);

  useEffect(() => {
    localStorage.setItem('poAdvancedSearchFilters', JSON.stringify(filters));
  }, [filters]);

  const handleSearch = useCallback(
    async (page = 1, limit = paginationLimitRef.current) => {
      setLoading(true);
      setError(null);
      try {
        const filterObj = {};
        filters.forEach((f) => {
          if (f.field && f.value) filterObj[f.field] = f.value;
        });
        if (dateFilter !== 'default') {
          filterObj.date_filter = dateFilter;
        }
        filterObj.query = searchValue;

        // Set after the advanced filters so the Status dropdown wins over an
        // advanced 'status' filter.
        if (statusFilter !== 'default') {
          filterObj.status = statusFilter;
        }

        const response = await purchaseOrdersService.searchByListField(
          filterObj,
          page,
          sortBy !== 'default' ? sortBy : undefined,
          sortOrder !== 'default' ? sortOrder : undefined,
          limit
        );

        if (response.success) {
          setPurchaseOrders(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPurchaseOrders([]);
          setError(response.message || 'Search failed. Please try again.');
        }
      } catch (err) {
        console.error('Failed to search purchase orders:', err);
        setError('Search failed. Please try again.');
      }
      setLoading(false);
    },
    [searchValue, filters, dateFilter, statusFilter, sortBy, sortOrder]
  );

  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  const debouncedSearch = useRef(
    debounce((page, limit) => handleSearchRef.current(page, limit), 500)
  ).current;

  // Keyed on [] deliberately: debouncedSearch is built with
  // useRef(debounce(...)).current, so its identity never changes and a
  // [debouncedSearch]-keyed cleanup would never fire.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Shared with PaginatedControls so paging uses the same search-vs-fetchAll
  // decision as this effect.
  const hasSearchCriteria =
    !!searchValue ||
    filters.length > 0 ||
    dateFilter !== 'default' ||
    statusFilter !== 'default' ||
    sortBy !== 'default' ||
    sortOrder !== 'default';

  useEffect(() => {
    // Reset to page 1 unconditionally, then either search or fall back to the
    // unfiltered list. The `else` is the fix: with no branch here, clearing
    // the last filter left the previous filtered results frozen on screen
    // while the search UI showed empty — it looked like clearing did nothing.
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (hasSearchCriteria) {
      debouncedSearch(1, pagination.limit);
    } else {
      // Cancel any in-flight debounced search so a slower stale response
      // cannot land afterwards and re-apply the filter just cleared.
      debouncedSearch.cancel();
      fetchAllPurchaseOrders(1, pagination.limit);
    }
  }, [
    searchValue,
    filters,
    dateFilter,
    statusFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    debouncedSearch,
    hasSearchCriteria,
    fetchAllPurchaseOrders
  ]);

  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'po_number':
        return 'PO Number';
      case 'po_title':
        return 'PO Title';
      case 'status':
        return 'Status';
      case 'supplier_id':
        return 'Supplier ID';
      case 'tracking_status':
        return 'Tracking Status';
      case 'ship_to':
        return 'Ship To';
      case 'has_tracking':
        return 'Has Tracking';
      default:
        return field;
    }
  };

  // Function to get status chip color based on status value
  const getStatusColor = (status) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'open':
        return 'primary';
      case 'closed':
        return 'success';
      case 'partial':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTrackingStatusColor = (status) => {
    if (!status) return 'default';
    const lower = status.toLowerCase();
    if (lower.includes('delivered')) return 'success';
    if (lower.includes('in transit') || lower.includes('arriving') || lower.includes('picked up') || lower.includes('delivery attempted')) return 'primary';
    if (lower.includes('checked in') || lower.includes('checkedin')) return 'info';
    if (lower.includes('exception') || lower.includes('delayed') || lower.includes('failed')) return 'error';
    if (lower.includes('created') || lower.includes('confirmed')) return 'warning';
    return 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filterOptions = [
    { value: 'po_number', label: 'PO Number' },
    { value: 'po_title', label: 'PO Title' },
    { value: 'status', label: 'Status' },
    { value: 'supplier_id', label: 'Supplier ID' },
    { value: 'tracking_status', label: 'Tracking Status' },
    { value: 'ship_to', label: 'Ship To' },
    { value: 'has_tracking', label: 'Has Tracking' }
  ];

  const sortByList = [
    { value: 'ID', label: 'ID' },
    { value: 'PONumber', label: 'PO Number' },
    { value: 'POTitle', label: 'PO Title' },
    { value: 'Status', label: 'Status' },
    { value: 'DateCreated', label: 'Date Created' },
    { value: 'TrackingStatus', label: 'Tracking Status' }
  ];

  return (
    <div>
      <div style={{ marginTop: 40, padding: 20 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Purchase Orders
        </Typography>
        <div style={{ margin: '20px auto', width: '80%' }}>
          <GenericSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            searchFieldLabel="PO Number / Title"
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            enableDateFilter={true}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            quickFilters={[
              {
                id: 'status-filter',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: PO_STATUS_OPTIONS
              }
            ]}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            handleSearchClick={handleSearchClick}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
            resetAll={() => {
              setSearchValue('');
              setFilters([]);
              setDateFilter('default');
              setStatusFilter('default');
              setSortBy('default');
              setSortOrder('default');
              setPagination({ currentPage: 1, totalPages: 1, limit: 20 });
              setShowAdvancedSearch(false);
              setFilterDraft({ field: '', value: '' });
              localStorage.removeItem('searchValue');
              localStorage.removeItem('advancedSearchFilters');
              fetchAllPurchaseOrders();
            }}
          />

          {(searchValue || filters.length > 0 || dateFilter !== 'default') && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
              searchFieldLabel="PO Number / Title"
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={purchaseOrders.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllPurchaseOrders}
            isSearchActive={hasSearchCriteria}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : purchaseOrders.length === 0 ? (
            <Paper sx={{ padding: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Records Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {hasSearchCriteria
                  ? 'No purchase orders match your search criteria. Try adjusting your filters.'
                  : 'No purchase orders available.'}
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>PO Number</TableCell>
                    <TableCell>PO Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tracking Status</TableCell>
                    <TableCell>Date Created</TableCell>
                    <TableCell>Supplier ID</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.map((po) => (
                    <TableRow key={po.ID}>
                      <TableCell>{po.ID}</TableCell>
                      <TableCell>{po.PONumber || 'N/A'}</TableCell>
                      <TableCell>{po.POTitle || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={po.Status || 'Unknown'}
                          color={getStatusColor(po.Status)}
                        />
                      </TableCell>
                      <TableCell>
                        {po.LatestTrackingStatus ? (
                          <Chip
                            label={po.LatestTrackingStatus}
                            color={getTrackingStatusColor(po.LatestTrackingStatus)}
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(po.DateCreated)}</TableCell>
                      <TableCell>{po.SupplierID || 'N/A'}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            navigate(`/purchase-orders/${po.ID}`)
                          }
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={purchaseOrders.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllPurchaseOrders}
            isSearchActive={hasSearchCriteria}
          />
        </div>
      </div>
    </div>
  );
}
